import React, { useRef, useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import WebViewer from "@pdftron/webviewer";
import styled from "styled-components";
import {
  fetchTemplateFile,
  fetchTemplateSignatureFields,
  saveTemplateFile,
  saveTemplateSignatureFields,
} from "../../../service/settings";
import { getAnnotationPdfPlacement } from "../../../utils/esignBoxPlacement";
import useAuth from "../../../hooks/useAuth";
import PlaceSignatureBoxes from "../../../components/ESignModal/PlaceSignatureBoxes";
import {
  TEMPLATE_BOX_ROLES,
  boxesToSavedFields,
  savedFieldsToBoxes,
} from "./templateSignatureBoxes";

const TEMPLATE_LOAD_TIMEOUT_MS = 30000;
const TEMPLATE_FETCH_TIMEOUT_MS = 30000;

const CONTENT_TYPE_EXTENSION_MAP = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/msword": "docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
};

const normalizeTemplateExtension = (value, fallback = "pdf") => {
  const rawValue = String(value || "").trim().toLowerCase();
  if (!rawValue) return fallback;

  const cleanValue = rawValue.replace(/^\./, "");
  if (
    cleanValue === "docx" ||
    cleanValue === "doc" ||
    cleanValue === "word" ||
    cleanValue === "document" ||
    cleanValue.includes("wordprocessingml") ||
    cleanValue.includes("msword")
  ) {
    return "docx";
  }
  if (
    cleanValue === "xlsx" ||
    cleanValue === "xls" ||
    cleanValue === "excel" ||
    cleanValue.includes("spreadsheetml")
  ) {
    return "xlsx";
  }
  if (cleanValue === "pdf" || cleanValue.includes("application/pdf")) {
    return "pdf";
  }

  return fallback;
};

const getResponseHeader = (headers, headerName) => {
  if (!headers) return "";
  if (typeof headers.get === "function") {
    return headers.get(headerName) || headers.get(headerName.toLowerCase()) || "";
  }
  return headers[headerName] || headers[headerName.toLowerCase()] || "";
};

const getExtensionFromContentDisposition = (contentDisposition) => {
  const match = String(contentDisposition || "").match(/filename\*?=(?:UTF-8'')?"?[^";]+\.([a-z0-9]+)"?/i);
  return match?.[1] || "";
};

const getExtensionFromFilename = (filename) => {
  const match = String(filename || "").match(/\.([a-z0-9]+)$/i);
  return match?.[1] || "";
};

const bytesToString = (bytes) => {
  let result = "";
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    result += String.fromCharCode(...bytes.slice(i, i + chunkSize));
  }
  return result;
};

const readBlobAsArrayBuffer = (blob) => {
  if (blob && typeof blob.arrayBuffer === "function") {
    return blob.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Could not read template file"));
    reader.readAsArrayBuffer(blob);
  });
};

const detectExtensionFromBlob = async (blob, fallback = "") => {
  if (!blob || typeof blob.slice !== "function") return "";

  const blobSlice = blob.slice(0, Math.min(blob.size || 0, 1024 * 1024));
  const bytes = new Uint8Array(await readBlobAsArrayBuffer(blobSlice));

  if (
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46
  ) {
    return "pdf";
  }

  const isZip = bytes[0] === 0x50 && bytes[1] === 0x4b;
  if (!isZip) return "";

  const zipText = bytesToString(bytes);
  if (zipText.includes("xl/")) return "xlsx";
  if (zipText.includes("word/")) return "docx";

  // A mislabelled Word template currently reaches this editor as "pdf".
  // Office files are ZIP containers, so prefer DOCX over PDF when the bytes prove ZIP.
  return fallback === "pdf" ? "docx" : fallback;
};

const resolveTemplateExtension = async (response, urlType) => {
  const fallback = normalizeTemplateExtension(urlType);
  const headerType = getResponseHeader(response?.headers, "X-Template-Type");
  const contentType = getResponseHeader(response?.headers, "Content-Type");
  const contentDisposition = getResponseHeader(response?.headers, "Content-Disposition");
  const filenameExtension = getExtensionFromContentDisposition(contentDisposition);
  const blobType = response?.data?.type;
  const blobExtension = await detectExtensionFromBlob(response?.data, fallback);

  return normalizeTemplateExtension(
    blobExtension ||
      headerType ||
      CONTENT_TYPE_EXTENSION_MAP[String(contentType || "").split(";")[0]?.toLowerCase()] ||
      filenameExtension ||
      CONTENT_TYPE_EXTENSION_MAP[String(blobType || "").split(";")[0]?.toLowerCase()] ||
      fallback,
    fallback
  );
};

const withTimeout = (promise, timeoutMs, message) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
};

const buildTemplateFilename = (templateName, extension) => {
  const safeName = String(templateName || "template")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .trim();
  return `${safeName || "template"}.${extension}`;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const ROUNDTRIP_TEMPLATE_CONFIG = {
  docx: {
    extension: "docx",
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    kindLabel: "Word",
    downloadLabel: "Download for Word",
    uploadLabel: "Upload Word Version",
    uploadingLabel: "Uploading...",
    accept: ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    downloadSuccess:
      "Word backup downloaded. Edit it in Word, then upload the updated file.",
    uploadSuccess: "Updated Word file uploaded to Formstack",
    uploadReloadError: "Uploaded to Formstack, but preview reload failed",
    invalidUploadMessage: "Please upload a .docx Word file",
  },
  pdf: {
    extension: "pdf",
    mimeType: "application/pdf",
    kindLabel: "PDF",
    downloadLabel: "Download PDF Backup",
    uploadLabel: "Upload PDF Version",
    uploadingLabel: "Uploading...",
    accept: ".pdf,application/pdf",
    downloadSuccess:
      "PDF backup downloaded. Edit it in your PDF editor, then upload the updated file.",
    uploadSuccess: "Updated PDF file uploaded to Formstack",
    uploadReloadError: "Uploaded to Formstack, but PDF preview reload failed",
    invalidUploadMessage: "Please upload a valid .pdf file",
  },
};

const isPdfFile = async (file) => {
  if (!file || typeof file.slice !== "function") return false;
  const bytes = new Uint8Array(await readBlobAsArrayBuffer(file.slice(0, 4)));
  return (
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46
  );
};

const SIGNATURE_ROLES = [
  ["client", "Client"],
  ["referral_attorney", "Referral Attorney"],
  ["handling_attorney", "Handling Attorney"],
  ["power_of_attorney", "Power of Attorney"],
];

const SIGNATURE_FIELD_TYPES = [
  ["signature", "Signature"],
  ["date", "Date"],
  ["initial", "Initial"],
  ["text", "Text"],
  ["stamped_image", "Stamped image (POA)"],
];

const DEFAULT_SIGNATURE_FIELD_WIDTH = 150;
const DEFAULT_SIGNATURE_FIELD_HEIGHT = 36;

const BLANK_SIGNATURE_FIELD = {
  role: "client",
  field_type: "signature",
  label: "",
  page_number: 1,
  x: 36,
  y: 36,
  width: DEFAULT_SIGNATURE_FIELD_WIDTH,
  height: DEFAULT_SIGNATURE_FIELD_HEIGHT,
  font_size: 1,
  required: true,
  hidden: true,
  order: 0,
  is_active: true,
};

const SIGNATURE_ROLE_LABELS = Object.fromEntries(SIGNATURE_ROLES);
const SIGNATURE_FIELD_TYPE_LABELS = Object.fromEntries(SIGNATURE_FIELD_TYPES);

const FORMSTACK_SIGN_TEXT_TAG_RE =
  /\[(sig|date|initial|text|check|fullname)\|(req|noreq)\|signer(\d+)(?:\|(width\d+))?\]/gi;

const FORMSTACK_TAG_TYPE_LABELS = {
  sig: "Signature",
  date: "Date",
  initial: "Initial",
  text: "Text",
  check: "Checkbox",
  fullname: "Full name",
};

const SIGNER_INDEX_LABELS = {
  1: "Client / Veteran (signer1)",
  2: "Referral or Handling Attorney (signer2)",
  3: "Handling Attorney on referral matters (signer3)",
};

const TEMPLATE_TAG_GUIDANCE = {
  pdf:
    "Use Add to place signature fields in the DMS Template Editor, then Save signature fields. PDF/Kofax source tags are not used for e-signature placement.",
  docx:
    "Place [sig|req|signer1] source tags inside the same Word conditional section as each signature block. Use signer2 for handling attorney on standard matters and signer2/signer3 inside referral branches.",
  default:
    "Use Formstack Sign text tags in the source template when possible. DMS detects them in the final PDF and skips coordinate stamping.",
};

const PDF_DMS_SIGNATURE_GUIDANCE =
  "Use Add to draw signature fields in the DMS Template Editor, then Save signature fields.";

const extractFormstackSignTagsFromText = (text) => {
  const tags = [];
  const seen = new Set();
  if (!text) return tags;

  const pattern = new RegExp(
    FORMSTACK_SIGN_TEXT_TAG_RE.source,
    FORMSTACK_SIGN_TEXT_TAG_RE.flags
  );
  let match = pattern.exec(text);
  while (match) {
    const rawTag = match[0];
    if (!seen.has(rawTag)) {
      seen.add(rawTag);
      tags.push({
        field_type: match[1].toLowerCase(),
        requirement: match[2].toLowerCase(),
        signer_index: Number(match[3]),
        width: (match[4] || "").toLowerCase(),
        raw_tag: rawTag,
      });
    }
    match = pattern.exec(text);
  }
  return tags;
};

const detectFormstackSignTagsFromBlob = async (blob, { maxBytes = 5 * 1024 * 1024 } = {}) => {
  if (!blob || typeof blob.slice !== "function") return [];
  const slice = blob.slice(0, Math.min(blob.size || 0, maxBytes));
  const bytes = new Uint8Array(await readBlobAsArrayBuffer(slice));
  return extractFormstackSignTagsFromText(bytesToString(bytes));
};

const formatDetectedTemplateTagLabel = (tag) => {
  const typeLabel = FORMSTACK_TAG_TYPE_LABELS[tag.field_type] || tag.field_type;
  const signerLabel =
    SIGNER_INDEX_LABELS[tag.signer_index] || `Signer ${tag.signer_index}`;
  return `${signerLabel} — ${typeLabel}`;
};

const formatSignatureFieldLabel = (field) => {
  const explicitLabel = String(field?.label || "").trim();
  if (explicitLabel) return explicitLabel;

  const roleLabel = SIGNATURE_ROLE_LABELS[field?.role] || "Signer";
  const typeLabel = SIGNATURE_FIELD_TYPE_LABELS[field?.field_type] || "Field";
  return `${roleLabel} ${typeLabel}`;
};


const getAnnotationFieldName = (annotation) => {
  try {
    const field = annotation?.getField?.() || annotation?.field || annotation?.Field;
    return (
      annotation?.fieldName ||
      annotation?.FieldName ||
      annotation?.fieldNameOverride ||
      field?.name ||
      field?.Name ||
      field?.fieldName ||
      field?.getName?.() ||
      annotation?.Id ||
      annotation?.id ||
      ""
    );
  } catch (error) {
    console.warn("Could not read signature field name", error);
    return "";
  }
};

const isNativeSignatureAnnotation = (annotation) => {
  const field = annotation?.getField?.() || annotation?.field || annotation?.Field;
  const values = [
    annotation?.Subject,
    annotation?.subject,
    annotation?.type,
    annotation?.Type,
    annotation?.elementName,
    annotation?.fieldType,
    annotation?.FieldType,
    field?.type,
    field?.Type,
    field?.fieldType,
    field?.getType?.(),
    getAnnotationFieldName(annotation),
  ]
    .map((value) => String(value || "").toLowerCase())
    .filter(Boolean);

  return values.some((value) => value.includes("signature") || value === "sig");
};

const getNativeSignatureAnnotations = (instance) => {
  const annotations =
    instance?.Core?.annotationManager?.getAnnotationsList?.() || [];

  return annotations
    .filter(isNativeSignatureAnnotation)
    .sort((a, b) => {
      const pageDelta = Number(a.PageNumber || 1) - Number(b.PageNumber || 1);
      if (pageDelta !== 0) return pageDelta;
      const yDelta = Number(a.Y || 0) - Number(b.Y || 0);
      if (yDelta !== 0) return yDelta;
      return Number(a.X || 0) - Number(b.X || 0);
    });
};

const getSignatureFormFieldToolName = (instance) =>
  instance?.Core?.Tools?.ToolNames?.SIG_FORM_FIELD || "SignatureFormFieldCreateTool";

const buildSignatureFieldsFromNativeAnnotations = (instance, savedFields = []) =>
  getNativeSignatureAnnotations(instance).map((annotation, index) => {
    const saved = savedFields[index] || {};
    const placement = getAnnotationPdfPlacement(instance, annotation) || {};
    const fieldName = getAnnotationFieldName(annotation);

    return {
      ...BLANK_SIGNATURE_FIELD,
      ...saved,
      ...placement,
      width:
        placement.width ??
        saved.width ??
        DEFAULT_SIGNATURE_FIELD_WIDTH,
      height:
        placement.height ??
        saved.height ??
        DEFAULT_SIGNATURE_FIELD_HEIGHT,
      label: saved.label || fieldName || formatSignatureFieldLabel(saved),
      order: index,
      is_active: true,
    };
  });

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #1a1a2e;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: #16213e;
  border-bottom: 1px solid #0f3460;
  min-height: 56px;
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BackButton = styled.button`
  background: none;
  border: 1px solid #4a5568;
  color: #e2e8f0;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover {
    background: #2d3748;
  }
`;

const TemplateName = styled.h2`
  color: #e2e8f0;
  font-size: 16px;
  margin: 0;
  font-weight: 500;
`;

const TemplateType = styled.span`
  color: #a0aec0;
  font-size: 12px;
  background: #2d3748;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
`;

const SaveButton = styled.button`
  background: #3182ce;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  &:hover {
    background: #2b6cb0;
  }
  &:disabled {
    background: #4a5568;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(SaveButton)`
  background: #2d3748;
  border: 1px solid #4a5568;

  &:hover {
    background: #374151;
  }

  &:disabled {
    background: #2d3748;
    opacity: 0.6;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const StatusText = styled.span`
  color: ${(p) => (p.$error ? "#fc8181" : p.$success ? "#68d391" : "#a0aec0")};
  font-size: 13px;
`;

const ViewerContainer = styled.div`
  flex: 1;
  position: relative;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1a1a2e;
  color: #e2e8f0;
  font-size: 16px;
  z-index: 10;
`;

const SignaturePlacementPanel = styled.aside`
  position: absolute;
  top: 12px;
  right: 12px;
  bottom: 12px;
  z-index: 20;
  width: min(440px, calc(100% - 24px));
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border: 1px solid #cbd5e0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.28);
  overflow: auto;
`;

const SignaturePanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
`;

const SignaturePanelTitle = styled.h3`
  margin: 0;
  color: #1a202c;
  font-size: 16px;
`;

const SignatureFieldsBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SignatureHelp = styled.p`
  margin: 0;
  color: #4a5568;
  font-size: 13px;
`;

const SignatureFieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  align-items: end;
  padding: 10px;
  border: 2px solid ${(p) => (p.$selected ? "#f6ad55" : "#e2e8f0")};
  border-radius: 8px;
  background: #f8fafc;
`;

const SignatureControl = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #2d3748;
  font-size: 12px;
  font-weight: 600;
`;

const SignatureInput = styled.input`
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  padding: 7px 8px;
  font-size: 13px;
`;

const SignatureSelect = styled.select`
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  padding: 7px 8px;
  font-size: 13px;
  background: white;
`;

const SignatureActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const SignatureModeBadge = styled.span`
  display: inline-block;
  margin-top: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  background: ${(p) => (p.$template ? "#c6f6d5" : "#feebc8")};
  color: ${(p) => (p.$template ? "#22543d" : "#7b341e")};
`;

const DetectedTagRow = styled.div`
  padding: 10px;
  border: 1px solid #bee3f8;
  border-radius: 8px;
  background: #ebf8ff;
  color: #2c5282;
  font-size: 13px;
`;

const DetectedTagCode = styled.code`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #1a365d;
`;

const SignatureWideControl = styled(SignatureControl)`
  grid-column: 1 / -1;
`;

const SignatureRowActions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
`;

const DeleteFieldButton = styled(SecondaryButton)`
  background: #742a2a;
  border-color: #9b2c2c;

  &:hover {
    background: #9b2c2c;
  }
`;

const TemplateEditor = () => {
  const { formstackId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const viewerRef = useRef(null);
  const instanceRef = useRef(null);
  const currentTemplateBlobRef = useRef(null);
  const templateUploadInputRef = useRef(null);
  const signatureModalOpenRef = useRef(false);
  const signatureFieldsRef = useRef([]);

  const templateName = searchParams.get("name") || `Template ${formstackId}`;
  const requestedTemplateType = normalizeTemplateExtension(searchParams.get("type"));
  const [templateType, setTemplateType] = useState(requestedTemplateType);
  const templateTypeRef = useRef(requestedTemplateType);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureFields, setSignatureFields] = useState([]);
  const [signatureFieldsLoading] = useState(false);
  const [signatureFieldsSaving, setSignatureFieldsSaving] = useState(false);
  const [selectedSignatureFieldIndex, setSelectedSignatureFieldIndex] = useState(0);
  const [signaturePlacementMode, setSignaturePlacementMode] = useState("legacy_manual");
  const [boxToolOpen, setBoxToolOpen] = useState(false);
  const { hasCapability } = useAuth();
  const canManageSignatureForms = hasCapability("esign.manage_forms");
  const [detectedTemplateTags, setDetectedTemplateTags] = useState([]);
  const [templateTagScanPending, setTemplateTagScanPending] = useState(false);

  useEffect(() => {
    signatureModalOpenRef.current = signatureModalOpen;
  }, [signatureModalOpen]);

  useEffect(() => {
    signatureFieldsRef.current = signatureFields;
  }, [signatureFields]);

  const scanTemplateTags = useCallback(async (blob = currentTemplateBlobRef.current) => {
    if (!blob) {
      setDetectedTemplateTags([]);
      return [];
    }

    setTemplateTagScanPending(true);
    try {
      const tags = await detectFormstackSignTagsFromBlob(blob);
      setDetectedTemplateTags(tags);
      if (templateTypeRef.current === "docx" && tags.length) {
        setSignaturePlacementMode("template_tags");
      } else if (templateTypeRef.current === "pdf") {
        setSignaturePlacementMode("legacy_manual");
      }
      return tags;
    } catch (error) {
      console.warn("Could not scan template for Formstack tags", error);
      setDetectedTemplateTags([]);
      return [];
    } finally {
      setTemplateTagScanPending(false);
    }
  }, []);

  const isPdfTemplate = templateType === "pdf";
  const isDocxTemplate = templateType === "docx";
  const isTemplateTagMode = signaturePlacementMode === "template_tags";
  const templateTagGuidance =
    TEMPLATE_TAG_GUIDANCE[templateType] || TEMPLATE_TAG_GUIDANCE.default;

  useEffect(() => {
    if (!viewerRef.current || instanceRef.current) return;

    let cancelled = false;
    let loadTimeout = null;

    const init = async () => {
      try {
        templateTypeRef.current = requestedTemplateType;
        currentTemplateBlobRef.current = null;
        setTemplateType(requestedTemplateType);
        setLoading(true);
        setDirty(false);
        setStatusMsg(null);

        const instance = await WebViewer(
          {
            path: "/webviewer/lib/",
            workerPath: "/webviewer/lib/core/",
            licenseKey:
              process.env.REACT_APP_WEBVIEWER_LICENSE_KEY ||
              process.env.REACT_APP_APRYSE_LICENSE,
            fullAPI: true,
            enableWebSocket: false,
            css: "/webviewer-custom.css",
          },
          viewerRef.current
        );

        if (cancelled) return;
        instanceRef.current = instance;

        const { documentViewer, annotationManager } = instance.Core;

        instance.UI.enableFeatures([instance.UI.Feature.ContentEdit]);
        instance.UI.setToolbarGroup("toolbarGroup-Forms");

        annotationManager.addEventListener("annotationChanged", (annotations, action) => {
          if (action === "add" || action === "modify" || action === "delete") {
            setDirty(true);
          }

          if (
            signatureModalOpenRef.current &&
            action === "add" &&
            (annotations || []).some(isNativeSignatureAnnotation)
          ) {
            const fieldsFromPdf = buildSignatureFieldsFromNativeAnnotations(
              instanceRef.current,
              signatureFieldsRef.current
            );
            const nextFields = fieldsFromPdf.length
              ? fieldsFromPdf
              : signatureFieldsRef.current;
            setSignatureFields(nextFields);
            setSelectedSignatureFieldIndex(
              nextFields.length ? nextFields.length - 1 : 0
            );
          }
        });

        const resp = await withTimeout(
          fetchTemplateFile(formstackId),
          TEMPLATE_FETCH_TIMEOUT_MS,
          "Template file fetch timeout"
        );
        if (cancelled) return;

        const blob = resp.data;
        if (!blob || Number(blob.size) === 0) {
          throw new Error("Template file is empty");
        }

        const ext = await resolveTemplateExtension(resp, requestedTemplateType);
        if (cancelled) return;
        templateTypeRef.current = ext;
        currentTemplateBlobRef.current = blob;
        setTemplateType(ext);
        await scanTemplateTags(blob);
        const filename = buildTemplateFilename(templateName, ext);
        const loadErrorEvent = instance.UI?.Events?.LOAD_ERROR;
        let loadCompleted = false;
        let loadFailed = false;
        let cleanupLoadListeners = () => {};

        const failLoad = (error) => {
          if (cancelled || loadCompleted || loadFailed) return;
          loadFailed = true;
          cleanupLoadListeners({ keepDocumentLoaded: true });
          setLoading(false);
          setStatusMsg({
            text: "Failed to load template file",
            error: true,
          });
          console.error("Failed to load template file:", error);
        };

        const onDocumentLoaded = () => {
          if (cancelled || loadCompleted) return;
          loadCompleted = true;
          cleanupLoadListeners();
          setLoading(false);
          setStatusMsg(null);
        };

        const onLoadError = (error) => failLoad(error);

        cleanupLoadListeners = ({ keepDocumentLoaded = false } = {}) => {
          if (loadTimeout) {
            clearTimeout(loadTimeout);
            loadTimeout = null;
          }
          if (!keepDocumentLoaded) {
            documentViewer.removeEventListener("documentLoaded", onDocumentLoaded);
          }
          if (loadErrorEvent) {
            instance.UI.removeEventListener(loadErrorEvent, onLoadError);
          }
        };

        documentViewer.addEventListener("documentLoaded", onDocumentLoaded);
        if (loadErrorEvent) {
          instance.UI.addEventListener(loadErrorEvent, onLoadError);
        }

        loadTimeout = setTimeout(() => {
          failLoad(new Error("Template load timeout"));
        }, TEMPLATE_LOAD_TIMEOUT_MS);

        try {
          const loadResult = instance.UI.loadDocument(blob, { filename, extension: ext });
          if (loadResult && typeof loadResult.catch === "function") {
            loadResult.catch(failLoad);
          }
        } catch (syncError) {
          failLoad(syncError);
        }
      } catch (err) {
        console.error("Failed to initialize template editor:", err);
        if (!cancelled) {
          setLoading(false);
          setStatusMsg({ text: "Failed to load template file", error: true });
        }
      }
    };

    init();

    return () => {
      cancelled = true;
      if (loadTimeout) {
        clearTimeout(loadTimeout);
      }
      if (instanceRef.current) {
        try {
          instanceRef.current.UI.dispose();
        } catch (_) {}
        instanceRef.current = null;
      }
    };
  }, [formstackId, requestedTemplateType, scanTemplateTags, templateName]);

  useEffect(() => {
    let cancelled = false;
    setSignatureFields([]);
    setSelectedSignatureFieldIndex(0);

    const loadPersistedSignatureFields = async () => {
      try {
        const res = await fetchTemplateSignatureFields(formstackId);
        if (cancelled) return;
        const fields = res?.data?.signature_fields || [];
        setSignatureFields(fields);
        setSelectedSignatureFieldIndex(0);
      } catch (err) {
        console.error("Failed to preload signature fields:", err);
      }
    };

    loadPersistedSignatureFields();

    return () => {
      cancelled = true;
    };
  }, [formstackId]);

  const saveCurrentTemplateFile = useCallback(async () => {
    const instance = instanceRef.current;
    if (!instance) {
      throw new Error("Template editor is not ready");
    }

    const { documentViewer, annotationManager } = instance.Core;
    const doc = documentViewer.getDocument();

    const xfdfString = await annotationManager.exportAnnotations();
    const data = await doc.getFileData({ xfdfString, flatten: false });
    const arr = new Uint8Array(data);

    const ext = normalizeTemplateExtension(templateTypeRef.current);
    const mimeMap = {
      pdf: "application/pdf",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };
    const mime = mimeMap[ext] || "application/octet-stream";

    const file = new File([arr], buildTemplateFilename(templateName, ext), { type: mime });
    await saveTemplateFile(formstackId, file);
    currentTemplateBlobRef.current = file;
    return file;
  }, [formstackId, templateName]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setStatusMsg(null);
    try {
      await saveCurrentTemplateFile();
      setDirty(false);
      setStatusMsg({ text: "Saved successfully", success: true });
      setTimeout(() => setStatusMsg(null), 3000);
    } catch (err) {
      console.error("Failed to save template:", err);
      setStatusMsg({ text: "Save failed — " + (err.message || "try again"), error: true });
    } finally {
      setSaving(false);
    }
  }, [saveCurrentTemplateFile]);

  const roundtripConfig = ROUNDTRIP_TEMPLATE_CONFIG[templateType] || null;

  const handleDownloadBackup = useCallback(async () => {
    const config = ROUNDTRIP_TEMPLATE_CONFIG[templateTypeRef.current];
    if (!config) return;

    let blob = currentTemplateBlobRef.current;
    if (!blob) {
      setStatusMsg({ text: "Template file is not ready yet", error: true });
      return;
    }

    const instance = instanceRef.current;
    if (instance) {
      try {
        const { documentViewer, annotationManager } = instance.Core;
        const doc = documentViewer.getDocument();
        const xfdfString = await annotationManager.exportAnnotations();
        const data = await doc.getFileData({ xfdfString, flatten: false });
        blob = new File(
          [new Uint8Array(data)],
          buildTemplateFilename(`${templateName} backup`, config.extension),
          { type: config.mimeType }
        );
      } catch (err) {
        console.error(`Failed to prepare ${config.kindLabel} backup:`, err);
        setStatusMsg({
          text: "Backup download failed — " + (err.message || "try again"),
          error: true,
        });
        return;
      }
    }

    downloadBlob(blob, buildTemplateFilename(`${templateName} backup`, config.extension));
    setStatusMsg({ text: config.downloadSuccess, success: true });
  }, [templateName]);

  const handleUploadTemplateClick = useCallback(() => {
    templateUploadInputRef.current?.click();
  }, []);

  const handleUploadTemplateFile = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      const config = ROUNDTRIP_TEMPLATE_CONFIG[templateTypeRef.current];
      if (!config) return;

      const uploadedExtension = getExtensionFromFilename(file.name).toLowerCase();
      if (uploadedExtension !== config.extension) {
        setStatusMsg({ text: config.invalidUploadMessage, error: true });
        return;
      }

      if (config.extension === "pdf" && !(await isPdfFile(file))) {
        setStatusMsg({ text: config.invalidUploadMessage, error: true });
        return;
      }

      if (dirty) {
        const ok = window.confirm(
          `You have unsaved in-browser edits. Uploading a ${config.kindLabel} file will replace them. Continue?`
        );
        if (!ok) return;
      }

      setUploadingTemplate(true);
      setStatusMsg(null);

      try {
        await saveTemplateFile(formstackId, file);
        currentTemplateBlobRef.current = file;
        templateTypeRef.current = config.extension;
        setTemplateType(config.extension);
        setDirty(false);
        await scanTemplateTags(file);

        const instance = instanceRef.current;
        if (instance) {
          try {
            const loadResult = instance.UI.loadDocument(file, {
              filename: buildTemplateFilename(templateName, config.extension),
              extension: config.extension,
            });
            if (loadResult && typeof loadResult.then === "function") {
              await loadResult;
            }
          } catch (reloadError) {
            console.error(`Failed to reload uploaded ${config.kindLabel} template:`, reloadError);
            setStatusMsg({
              text: config.uploadReloadError,
              error: true,
            });
            return;
          }
        }

        setStatusMsg({
          text: config.uploadSuccess,
          success: true,
        });
      } catch (err) {
        console.error("Failed to upload Word template:", err);
        setStatusMsg({
          text: "Upload failed — " + (err.message || "try again"),
          error: true,
        });
      } finally {
        setUploadingTemplate(false);
      }
    },
    [dirty, formstackId, scanTemplateTags, templateName]
  );

  const syncSignatureFieldsFromPdf = useCallback(
    (
      savedFields = signatureFields,
      { preserveSavedWhenEmpty = false, selectLast = false } = {}
    ) => {
      const fieldsFromPdf = buildSignatureFieldsFromNativeAnnotations(
        instanceRef.current,
        savedFields
      );
      const nextFields = fieldsFromPdf.length
        ? fieldsFromPdf
        : preserveSavedWhenEmpty
          ? savedFields
          : [];
      setSignatureFields(nextFields);
      setSelectedSignatureFieldIndex((selected) => {
        if (selectLast && nextFields.length) return nextFields.length - 1;
        return nextFields.length ? Math.min(selected, nextFields.length - 1) : 0;
      });
      return nextFields;
    },
    [signatureFields]
  );

  const activateSignatureFieldCreateTool = useCallback(() => {
    const instance = instanceRef.current;
    if (!instance) return;

    instance.UI.setToolbarGroup("toolbarGroup-Forms");
    instance.UI.setToolMode(getSignatureFormFieldToolName(instance));
  }, []);

  const updateSignatureField = useCallback((index, key, value) => {
    setSignatureFields((prev) =>
      prev.map((field, fieldIndex) =>
        fieldIndex === index ? { ...field, [key]: value } : field
      )
    );
  }, []);

  const removeSignatureField = useCallback((index) => {
    const instance = instanceRef.current;
    const nativeAnnotations = getNativeSignatureAnnotations(instance);
    const annotation = nativeAnnotations[index];
    const annotationManager = instance?.Core?.annotationManager;

    if (annotation && typeof annotationManager?.deleteAnnotations === "function") {
      annotationManager.deleteAnnotations([annotation]);
      setDirty(true);
    }

    setSignatureFields((prev) => {
      const next = prev.filter((_, fieldIndex) => fieldIndex !== index);
      setSelectedSignatureFieldIndex((selected) => {
        if (next.length === 0) return 0;
        if (selected > index) return selected - 1;
        return Math.min(selected, next.length - 1);
      });
      return next;
    });
  }, []);

  const handleSaveSignatureFields = useCallback(async () => {
    setSignatureFieldsSaving(true);
    setStatusMsg(null);
    try {
      if (dirty) {
        await saveCurrentTemplateFile();
        setDirty(false);
      }
      const syncedFields = syncSignatureFieldsFromPdf(signatureFields);
      const fieldsToSave = syncedFields.length ? syncedFields : [];
      const finalPayload = fieldsToSave.map((field, index) => ({
        ...field,
        page_number: Number(field.page_number) || 1,
        x: field.x === "" ? 36 : Number(field.x),
        y: field.y === "" ? 36 : Number(field.y),
        width:
          Number(field.width) > 0
            ? Number(field.width)
            : DEFAULT_SIGNATURE_FIELD_WIDTH,
        height:
          Number(field.height) > 0
            ? Number(field.height)
            : DEFAULT_SIGNATURE_FIELD_HEIGHT,
        font_size: Number(field.font_size) || 1,
        order: index,
        required: field.required !== false,
        hidden: field.hidden !== false,
        is_active: field.is_active !== false,
      }));
      const res = await saveTemplateSignatureFields(formstackId, finalPayload);
      setSignatureFields(res?.data?.signature_fields || finalPayload);
      setSignatureModalOpen(false);
      setStatusMsg({
        text: dirty
          ? "Template and signature field properties saved"
          : "Signature field properties saved",
        success: true,
      });
      setTimeout(() => setStatusMsg(null), 3000);
    } catch (err) {
      console.error("Failed to save signature fields:", err);
      setStatusMsg({
        text:
          "Signature fields save failed — " +
          (err?.response?.data?.error ||
            err?.response?.data?.errors?.join(", ") ||
            err.message ||
            "try again"),
        error: true,
      });
    } finally {
      setSignatureFieldsSaving(false);
    }
  }, [
    dirty,
    formstackId,
    saveCurrentTemplateFile,
    signatureFields,
    syncSignatureFieldsFromPdf,
  ]);

  const resolveTemplatePdf = useCallback(() => {
    const blob = currentTemplateBlobRef.current;
    if (!blob) return null;
    return {
      url: URL.createObjectURL(blob),
      isDirect: true,
      withCredentials: false,
      fileName: buildTemplateFilename(templateName, "pdf"),
      contentType: "application/pdf",
    };
  }, [templateName]);

  const handleSaveSignatureBoxes = useCallback(
    async (boxes) => {
      setBoxToolOpen(false);
      setStatusMsg(null);
      try {
        const res = await saveTemplateSignatureFields(
          formstackId,
          boxesToSavedFields(boxes),
          { scope: "esign" }
        );
        setSignatureFields(res?.data?.signature_fields || []);
        setStatusMsg({
          text: boxes.length
            ? `Signature boxes saved (${boxes.length}). Documents made from this template are ready to sign.`
            : "Signature boxes removed from this template.",
          success: true,
        });
        setTimeout(() => setStatusMsg(null), 5000);
      } catch (err) {
        setStatusMsg({
          text:
            "Signature boxes were not saved — " +
            (err?.response?.data?.error || err.message || "try again"),
          error: true,
        });
      }
    },
    [formstackId]
  );

  const handleBack = useCallback(() => {
    if (dirty) {
      const ok = window.confirm(
        "You have unsaved changes. Leave without saving?"
      );
      if (!ok) return;
    }
    navigate("/settings");
  }, [dirty, navigate]);

  return (
    <Page>
      <Toolbar>
        <ToolbarLeft>
          <BackButton onClick={handleBack}>&larr; Back</BackButton>
          <TemplateName>{templateName}</TemplateName>
          <TemplateType>{templateType}</TemplateType>
        </ToolbarLeft>
        <ToolbarRight>
          {statusMsg && (
            <StatusText $error={statusMsg.error} $success={statusMsg.success}>
              {statusMsg.text}
            </StatusText>
          )}
          {roundtripConfig && (
            <>
              <SecondaryButton
                onClick={handleDownloadBackup}
                disabled={loading || saving || uploadingTemplate}
              >
                {roundtripConfig.downloadLabel}
              </SecondaryButton>
              <SecondaryButton
                onClick={handleUploadTemplateClick}
                disabled={loading || saving || uploadingTemplate}
              >
                {uploadingTemplate ? roundtripConfig.uploadingLabel : roundtripConfig.uploadLabel}
              </SecondaryButton>
              <HiddenFileInput
                ref={templateUploadInputRef}
                type="file"
                accept={roundtripConfig.accept}
                onChange={handleUploadTemplateFile}
              />
            </>
          )}
          {isPdfTemplate && canManageSignatureForms && (
            <SecondaryButton
              type="button"
              data-testid="template-signature-boxes-button"
              onClick={() => setBoxToolOpen(true)}
              disabled={loading || !currentTemplateBlobRef.current}
            >
              Signature boxes
              {savedFieldsToBoxes(signatureFields).length
                ? ` (${savedFieldsToBoxes(signatureFields).length})`
                : ""}
            </SecondaryButton>
          )}
          <SaveButton onClick={handleSave} disabled={saving || uploadingTemplate || !dirty}>
            {saving ? "Saving..." : "Save to Formstack"}
          </SaveButton>
        </ToolbarRight>
      </Toolbar>
      <ViewerContainer>
        {loading && (
          <LoadingOverlay>Loading template editor...</LoadingOverlay>
        )}
        <div
          ref={viewerRef}
          style={{ width: "100%", height: "100%", position: "absolute" }}
        />
        {false && signatureModalOpen && (
          <SignaturePlacementPanel data-testid="signature-placement-panel">
            <SignaturePanelHeader>
              <div>
                <SignaturePanelTitle>Signature properties</SignaturePanelTitle>
                <SignatureModeBadge
                  $template={isTemplateTagMode}
                  data-testid="signature-placement-mode-badge"
                >
                  {isTemplateTagMode
                    ? "Template tags"
                    : isPdfTemplate
                      ? "DMS signature fields"
                      : "Legacy/manual placement"}
                </SignatureModeBadge>
                <SignatureHelp data-testid="signature-mode-help">
                  {isTemplateTagMode
                    ? "Formstack Sign text tags in the source Word template drive field placement. DMS sends the final PDF unchanged."
                    : isPdfTemplate
                      ? PDF_DMS_SIGNATURE_GUIDANCE
                      : "Use Apryse Add/Delete for legacy coordinate placement when the source template has no Formstack tags."}
                </SignatureHelp>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                {!isTemplateTagMode && (
                  <SecondaryButton
                    type="button"
                    data-testid="signature-add-button"
                    onClick={activateSignatureFieldCreateTool}
                  >
                    Add
                  </SecondaryButton>
                )}
                <SecondaryButton type="button" onClick={() => setSignatureModalOpen(false)}>
                  Close
                </SecondaryButton>
              </div>
            </SignaturePanelHeader>
            <SignatureFieldsBody>
              {isDocxTemplate ? (
                <SignatureWideControl>
                  Placement mode
                  <SignatureSelect
                    data-testid="signature-placement-mode-select"
                    value={signaturePlacementMode}
                    onChange={(event) => setSignaturePlacementMode(event.target.value)}
                  >
                    <option value="template_tags">Use template tags (recommended)</option>
                    <option value="legacy_manual">Legacy/manual coordinates</option>
                  </SignatureSelect>
                </SignatureWideControl>
              ) : isPdfTemplate ? (
                <SignatureHelp data-testid="signature-pdf-mode-guidance">
                  PDF templates use DMS signature fields. Template-tag mode is available
                  only for Word templates.
                </SignatureHelp>
              ) : (
                <SignatureWideControl>
                  Placement mode
                  <SignatureSelect
                    data-testid="signature-placement-mode-select"
                    value={signaturePlacementMode}
                    onChange={(event) => setSignaturePlacementMode(event.target.value)}
                  >
                    <option value="template_tags">Use template tags (recommended)</option>
                    <option value="legacy_manual">Legacy/manual coordinates</option>
                  </SignatureSelect>
                </SignatureWideControl>
              )}

              {templateTagScanPending ? (
                <SignatureHelp>Scanning template for Formstack Sign tags...</SignatureHelp>
              ) : null}

              {isPdfTemplate && detectedTemplateTags.length > 0 ? (
                <SignatureHelp data-testid="signature-pdf-detected-tags-warning">
                  Formstack tags were detected in this PDF, but PDF templates must use DMS
                  signature fields instead of source tags. Click Add to place fields here.
                </SignatureHelp>
              ) : null}

              {isTemplateTagMode ? (
                <>
                  <SignatureHelp data-testid="signature-template-tag-guidance">
                    {templateTagGuidance}
                  </SignatureHelp>
                  {detectedTemplateTags.length === 0 ? (
                    <SignatureHelp>
                      No Formstack tags were detected in this file yet. Add tags such as
                      {" "}
                      <code>[sig|req|signer1]</code>
                      {" "}
                      inside Word conditional sections, then re-upload or regenerate the
                      preview.
                    </SignatureHelp>
                  ) : (
                    detectedTemplateTags.map((tag, index) => (
                      <DetectedTagRow
                        key={`${tag.raw_tag}-${index}`}
                        data-testid={`detected-template-tag-${index}`}
                      >
                        {formatDetectedTemplateTagLabel(tag)}
                        <DetectedTagCode>{tag.raw_tag}</DetectedTagCode>
                      </DetectedTagRow>
                    ))
                  )}
                  <SignatureHelp>
                    Coordinate editing is disabled in template-tag mode. Referral matters
                    use signer1 client, signer2 referral attorney, and signer3 handling
                    attorney. Standard matters use signer1 client and signer2 handling
                    attorney.
                  </SignatureHelp>
                </>
              ) : signatureFieldsLoading ? (
                <SignatureHelp>Loading signature fields...</SignatureHelp>
              ) : signatureFields.length === 0 ? (
                <SignatureHelp>
                  No native signature fields were found in this template. Click Add, draw a
                  field on the document, or use Refresh from PDF fields after placing one
                  in the Apryse toolbar.
                </SignatureHelp>
              ) : (
                signatureFields.map((field, index) => (
                  <SignatureFieldRow
                    key={field.id || index}
                    $selected={selectedSignatureFieldIndex === index}
                    onClick={() => setSelectedSignatureFieldIndex(index)}
                  >
                    <SignatureControl>
                      Role
                      <SignatureSelect
                        value={field.role || "client"}
                        onChange={(event) => updateSignatureField(index, "role", event.target.value)}
                      >
                        {SIGNATURE_ROLES.map(([value, label]) => (
                          <option key={value} value={value}>
                            {value === "client" ? "Client / Veteran" : label}
                          </option>
                        ))}
                      </SignatureSelect>
                    </SignatureControl>
                    <SignatureControl>
                      Type
                      <SignatureSelect
                        value={field.field_type || "signature"}
                        onChange={(event) => updateSignatureField(index, "field_type", event.target.value)}
                      >
                        {SIGNATURE_FIELD_TYPES.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </SignatureSelect>
                    </SignatureControl>
                    <SignatureWideControl>
                      Label
                      <SignatureInput
                        value={field.label || ""}
                        placeholder="Client signature"
                        onChange={(event) => updateSignatureField(index, "label", event.target.value)}
                      />
                    </SignatureWideControl>
                    <SignatureControl>
                      Page
                      <SignatureInput
                        data-testid={`signature-page-${index}`}
                        type="number"
                        min="1"
                        value={field.page_number || 1}
                        readOnly
                      />
                    </SignatureControl>
                    <SignatureControl>
                      X / Y
                      <div style={{ display: "flex", gap: 6 }}>
                        <SignatureInput
                          data-testid={`signature-x-${index}`}
                          value={field.x ?? 36}
                          readOnly
                        />
                        <SignatureInput
                          data-testid={`signature-y-${index}`}
                          value={field.y ?? 36}
                          readOnly
                        />
                      </div>
                    </SignatureControl>
                    <SignatureControl>
                      Font size
                      <SignatureInput
                        type="number"
                        min="1"
                        value={field.font_size || 1}
                        onChange={(event) => updateSignatureField(index, "font_size", event.target.value)}
                      />
                    </SignatureControl>
                    <SignatureControl>
                      Options
                      <SignatureSelect
                        value={`${field.required === false ? "optional" : "required"}:${field.hidden === false ? "visible" : "hidden"}`}
                        onChange={(event) => {
                          const [requiredValue, hiddenValue] = event.target.value.split(":");
                          updateSignatureField(index, "required", requiredValue !== "optional");
                          updateSignatureField(index, "hidden", hiddenValue !== "visible");
                        }}
                      >
                        <option value="required:hidden">Required hidden</option>
                        <option value="required:visible">Required visible</option>
                        <option value="optional:hidden">Optional hidden</option>
                        <option value="optional:visible">Optional visible</option>
                      </SignatureSelect>
                    </SignatureControl>
                    <SignatureRowActions>
                      <DeleteFieldButton
                        type="button"
                        data-testid={`signature-delete-${index}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          removeSignatureField(index);
                        }}
                      >
                        Delete
                      </DeleteFieldButton>
                    </SignatureRowActions>
                  </SignatureFieldRow>
                ))
              )}
              {!isTemplateTagMode ? (
                <SignatureActions>
                  <SignatureHelp>
                    DMS reads page/X/Y from the native PDF field. Save this panel after
                    adding or moving fields.
                  </SignatureHelp>
                  <SecondaryButton type="button" onClick={() => syncSignatureFieldsFromPdf()}>
                    Refresh from PDF fields
                  </SecondaryButton>
                </SignatureActions>
              ) : null}
              {!isTemplateTagMode ? (
                <SignatureActions>
                  <SecondaryButton
                    type="button"
                    onClick={() => setSignatureModalOpen(false)}
                    disabled={signatureFieldsSaving}
                  >
                    Cancel
                  </SecondaryButton>
                  <SaveButton
                    type="button"
                    onClick={handleSaveSignatureFields}
                    disabled={signatureFieldsSaving || signatureFieldsLoading}
                  >
                    {signatureFieldsSaving ? "Saving..." : "Save signature fields"}
                  </SaveButton>
                </SignatureActions>
              ) : (
                <SignatureActions>
                  <SecondaryButton type="button" onClick={() => setSignatureModalOpen(false)}>
                    Close
                  </SecondaryButton>
                </SignatureActions>
              )}
            </SignatureFieldsBody>
          </SignaturePlacementPanel>
        )}
      </ViewerContainer>
      {isPdfTemplate && canManageSignatureForms && (
        <PlaceSignatureBoxes
          open={boxToolOpen}
          title={`Signature boxes — ${templateName || "template"}`}
          signerHeading="1. Which role signs this box"
          document={{ id: `template-${formstackId}`, name: buildTemplateFilename(templateName, "pdf") }}
          recipients={TEMPLATE_BOX_ROLES}
          initialBoxes={savedFieldsToBoxes(signatureFields)}
          resolveSource={resolveTemplatePdf}
          onCancel={() => setBoxToolOpen(false)}
          onDone={handleSaveSignatureBoxes}
        />
      )}
    </Page>
  );
};

export default TemplateEditor;
